import React, { useState, useEffect } from 'react';
import { FiPackage, FiPlus, FiMinus, FiTrash2, FiX, FiTag } from 'react-icons/fi';
import API, { getImagenUrl } from 'shared/api';
import { fmtMonto } from 'shared/format';
import { useAuth } from 'shared/context/AuthContext';

function isDark() {
  try {
    return document.documentElement.getAttribute('data-dark') === 'true';
  } catch {
    return false;
  }
}

export const kgToDisplay = (cant) => {
  const kg = cant / 1000;
  return kg.toFixed(3).replace('.', ',');
};

export const displayToGramos = (v) => {
  const n = v.replace(',', '.');
  const kg = parseFloat(n);
  if (isNaN(kg) || kg <= 0) return null;
  return Math.round(kg * 1000);
};

const cantBtnStyle = {
  background: 'var(--agx-skeleton)',
  border: 'none',
  borderRadius: '4px',
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
};

const cantTextStyle = {
  fontSize: '13px',
  fontWeight: 600,
  minWidth: '24px',
  textAlign: 'center',
  color: 'var(--agx-text)',
};

const ProductoItem = ({
  producto,
  index,
  esEditable,
  pesosLocales,
  onCantidadChange,
  onPesoChange,
  onEliminar,
  onEtiquetasChange,
  onPersonalizarCombo,
}) => {
  const { tieneVisibilidad } = useAuth();
  const v = (c) => tieneVisibilidad(c);

  const p = producto;
  const nombre = p.nombre || p.descripcion || `Item ${index + 1}`;
  const codigo = p.codigo || '';
  const referencia = p.referencia || '';
  const precio = p.precio || 0;
  const cant = p.cantidad || 1;
  const esPesable = p.pesable || false;
  const precioBase = esPesable ? (precio * cant) / 1000 : precio * cant;
  const stockMax = esPesable ? (p.stock || 0) * 1000 : p.stock || 0;
  const alcanzoMax = stockMax > 0 && cant >= stockMax;
  const etiquetas = p.etiquetas || [];
  const etiqExtraCalc = etiquetas.reduce((t, e) => t + (e.precio || 0), 0);
  const etiqExtra = p.etiquetas_extra || etiqExtraCalc || 0;
  const subtotal = precioBase + etiqExtra;
  const imgSrc = getImagenUrl(p.imagen_url);
  const hasEtiquetas = v('vis.pedidos.etiquetas') && etiquetas.length > 0;

  // Edicion de personalizaciones desde el panel de revision.
  const esCombo = codigo && String(codigo).startsWith('combo_');
  const [etiquetasDisp, setEtiquetasDisp] = useState([]);
  const [manualInput, setManualInput] = useState('');
  const [mostrarPers, setMostrarPers] = useState(false);

  useEffect(() => {
    if (!esEditable || !codigo || esCombo) { setEtiquetasDisp([]); return; }
    let activo = true;
    API.get(`/pedidos/producto/${encodeURIComponent(codigo)}/etiquetas`)
      .then((r) => {
        if (activo && r.data.status === 'success') setEtiquetasDisp(r.data.data || []);
      })
      .catch(() => {});
    return () => { activo = false; };
  }, [esEditable, codigo, esCombo]);

  const manuales = etiquetas.filter((e) => typeof e.id === 'string' && e.id.startsWith('manual_'));
  const predefSel = etiquetas.filter((e) => !(typeof e.id === 'string' && e.id.startsWith('manual_')));
  const esPredefSeleccionada = (id) => predefSel.some((e) => e.id === id);

  const toggleEtiqueta = (et) => {
    const existe = esPredefSeleccionada(et.id);
    const next = existe
      ? etiquetas.filter((e) => e.id !== et.id)
      : [...etiquetas, { id: et.id, nombre: et.nombre, precio: et.precio || 0 }];
    onEtiquetasChange && onEtiquetasChange(index, next);
  };

  const agregarManual = () => {
    const nom = manualInput.trim();
    if (!nom) return;
    onEtiquetasChange && onEtiquetasChange(index, [
      ...etiquetas,
      { id: 'manual_' + Date.now().toString(36), nombre: nom, precio: 0 },
    ]);
    setManualInput('');
  };

  const quitarManual = (id) => {
    onEtiquetasChange && onEtiquetasChange(index, etiquetas.filter((e) => e.id !== id));
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        backgroundColor: 'var(--agx-surface)',
        borderRadius: '8px',
        padding: '8px 12px',
        border: '1px solid var(--agx-border)',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 28,
          height: 28,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 2,
        }}
      >
        <FiPackage size={16} style={{ color: 'var(--agx-pri2)', position: 'absolute' }} />
        {imgSrc && (
          <img
            src={imgSrc}
            alt={nombre}
            onError={(e) => e.target.remove()}
            style={{
              position: 'relative',
              zIndex: 1,
              width: 28,
              height: 28,
              borderRadius: 6,
              objectFit: 'cover',
              background: 'var(--agx-surface)',
            }}
          />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '6px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--agx-text)' }}>
              {nombre}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px', flexWrap: 'wrap' }}>
              {codigo && (
                <span style={{ fontSize: '10px', color: 'var(--agx-faint)', backgroundColor: 'var(--agx-skeleton)', padding: '1px 5px', borderRadius: '4px' }}>
                  {codigo}
                </span>
              )}
              {referencia && (
                <span style={{ fontSize: '10px', color: 'var(--agx-muted)', backgroundColor: '#f3f4f6', padding: '1px 5px', borderRadius: '4px', fontWeight: 500 }}>
                  {referencia}
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              backgroundColor: isDark() ? 'rgba(41,82,204,0.15)' : 'var(--agx-blue-bg)',
              borderRadius: '8px',
              padding: '4px 10px',
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: '12px', color: 'var(--agx-faint)', fontWeight: 400 }}>x</span>
            <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--agx-pri2)', lineHeight: 1 }}>
              {esPesable ? kgToDisplay(cant).replace(',', '.') : cant}
            </span>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--agx-pri2)' }}>
              {esPesable ? 'kg' : 'und'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '11px' }}>
            <span style={{ color: 'var(--agx-muted)' }}>
              × ${fmtMonto(precio)} c/u = <span style={{ fontWeight: 600, color: 'var(--agx-pri2)', fontSize: '12px' }}>${fmtMonto(precioBase)}</span>
            </span>
            {p.stock !== undefined && p.stock !== null && (
              <span
                style={{
                  fontSize: '10px',
                  padding: '1px 6px',
                  borderRadius: '4px',
                  fontWeight: 600,
                  backgroundColor:
                    p.stock > 10 ? (isDark() ? '#064e3b' : '#f0fdf4') :
                    p.stock > 0 ? (isDark() ? '#78350f' : '#fffbeb') :
                    (isDark() ? '#7f1d1d' : '#fef2f2'),
                  color:
                    p.stock > 10 ? (isDark() ? '#6ee7b7' : '#166534') :
                    p.stock > 0 ? (isDark() ? '#fcd34d' : '#92400e') :
                    (isDark() ? '#fca5a5' : '#991b1b'),
                }}
              >
                Stock: {esPesable ? `${p.stock.toFixed(1)} kg` : p.stock}
              </span>
            )}
          </div>
        </div>
        {hasEtiquetas && (
          <div style={{ marginTop: '4px', paddingLeft: '2px' }}>
            {etiquetas.map((et, ei) => (
              <div
                key={et.id || ei}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '11px',
                  padding: '2px 0',
                }}
              >
                <span
                  style={{
                    color: 'var(--agx-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: '#ff6d00',
                      flexShrink: 0,
                    }}
                  />
                  {et.nombre}
                </span>
                {et.precio > 0 && (
                  <span
                    style={{
                      fontWeight: 600,
                      color: '#ff6d00',
                      fontSize: '11px',
                      marginLeft: '12px',
                      flexShrink: 0,
                    }}
                  >
                              +${fmtMonto(et.precio)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {esEditable && !esCombo && (
          <div style={{ marginTop: '6px' }}>
            <button
              onClick={() => setMostrarPers((v) => !v)}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: 'var(--agx-pri2)',
                fontSize: '11px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: 0,
                fontFamily: 'inherit',
              }}
            >
              <FiTag size={12} /> Personalizar ({etiquetas.length})
            </button>
            {mostrarPers && (
              <div style={{ marginTop: '6px', padding: '8px', background: 'var(--agx-bg2)', borderRadius: '8px' }}>
                {(etiquetasDisp.length > 0 || manuales.length > 0) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    {etiquetasDisp.map((et) => {
                      const sel = esPredefSeleccionada(et.id);
                      return (
                        <button
                          key={et.id}
                          onClick={() => toggleEtiqueta(et)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 9px',
                            borderRadius: '999px',
                            cursor: 'pointer',
                            border: sel ? '2px solid var(--agx-pri2)' : '1px solid var(--agx-border)',
                            background: sel ? 'var(--agx-pri2)' : 'var(--agx-surface)',
                            color: sel ? '#fff' : 'var(--agx-text)',
                            fontSize: '11px',
                            fontWeight: sel ? 700 : 500,
                            fontFamily: 'inherit',
                          }}
                        >
                          {et.nombre}
                          {et.precio > 0 && (
                            <span style={{ opacity: 0.9, fontWeight: 700 }}>
                    +${fmtMonto(et.precio)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {manuales.map((e) => (
                      <span
                        key={e.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '4px 9px',
                          borderRadius: '999px',
                          background: '#fef7e0',
                          color: '#b06000',
                          fontSize: '11px',
                          fontWeight: 600,
                          border: '1px solid #f3d78a',
                        }}
                      >
                        {e.nombre}
                        <button
                          onClick={() => quitarManual(e.id)}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#b06000', padding: 0, display: 'flex' }}
                        >
                          <FiX size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    value={manualInput}
                    onChange={(e) => setManualInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarManual(); } }}
                    placeholder="Nota manual (ej: sin cebolla)"
                    maxLength={80}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      border: '1px solid var(--agx-border)',
                      borderRadius: '8px',
                      fontSize: '11px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      background: 'var(--agx-surface)',
                      color: 'var(--agx-text)',
                    }}
                  />
                  <button
                    onClick={agregarManual}
                    disabled={!manualInput.trim()}
                    style={{
                      padding: '0 12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'var(--agx-pri2)',
                      color: '#fff',
                      fontSize: '11px',
                      fontWeight: 700,
                      cursor: manualInput.trim() ? 'pointer' : 'default',
                      opacity: manualInput.trim() ? 1 : 0.5,
                      fontFamily: 'inherit',
                    }}
                  >
                    Agregar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {p.combo_id && Array.isArray(p.componentes) && p.componentes.length > 0 && (
          <div style={{ marginTop: '5px', paddingLeft: '2px' }}>
            {esEditable && onPersonalizarCombo && (
              <button
                onClick={() => onPersonalizarCombo(index, p)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--agx-pri2)',
                  fontSize: '11px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: 0,
                  marginBottom: '4px',
                  fontFamily: 'inherit',
                }}
              >
                <FiTag size={12} /> Personalizar combo
              </button>
            )}
            {p.componentes.map((comp, ci) => {
              const compEtiq = Array.isArray(comp.etiquetas) ? comp.etiquetas : [];
              return (
                <div
                  key={ci}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '11px',
                    padding: '2px 0',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: '#f0b400',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: 'var(--agx-muted)' }}>{comp.nombre}</span>
                  {compEtiq.length > 0 && (
                    <span style={{ color: 'var(--agx-faint)' }}>
                      ({compEtiq.map((e) => e.nombre).join(', ')})
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {esEditable ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          {esPesable ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <input
                value={pesosLocales[index] || kgToDisplay(cant)}
                onChange={(e) => onPesoChange(index, e.target.value)}
                style={{
                  width: '68px',
                  padding: '3px 6px',
                  border: '1px solid var(--agx-border)',
                  borderRadius: '4px',
                  fontSize: '11px',
                  textAlign: 'center',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
                placeholder="0,000"
              />
              <span style={{ fontSize: '11px', color: 'var(--agx-muted)' }}>kg</span>
            </div>
          ) : (
            <>
              <button
                onClick={() => onCantidadChange(index, -1)}
                disabled={cant <= 1}
                style={{ ...cantBtnStyle, opacity: cant <= 1 ? 0.3 : 1 }}
              >
                <FiMinus size={12} />
              </button>
              <span style={cantTextStyle}>{cant}</span>
              <button
                onClick={() => onCantidadChange(index, 1)}
                disabled={alcanzoMax}
                style={{ ...cantBtnStyle, opacity: alcanzoMax ? 0.3 : 1 }}
              >
                <FiPlus size={12} />
              </button>
            </>
          )}
          <button
            onClick={() => onEliminar(index)}
            style={{ ...cantBtnStyle, color: '#ea4335' }}
          >
            <FiTrash2 size={12} />
          </button>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: 'var(--agx-pri2)',
              whiteSpace: 'nowrap',
              marginLeft: '4px',
            }}
          >
            ${fmtMonto(subtotal)}
          </span>
        </div>
      ) : (
        <span
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--agx-pri2)',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            marginTop: 2,
          }}
        >
          ${fmtMonto(subtotal)}
        </span>
      )}
    </div>
  );
};

export default ProductoItem;
